import { Component, type ErrorInfo, type ReactNode } from "react";

/**
 * A last-resort error boundary so a render error in one screen does not blank the whole app. The
 * shell is meant to always load and stay informative (NFR-7 AC1); if a screen throws, the operator
 * still sees a readable message and a reload path rather than a white page.
 *
 * It is intentionally plain: no telemetry wiring here (the SPA emits none), just a contained
 * fallback. A finer-grained boundary around each route can be added when the screens exist.
 */

interface Props {
  readonly children: ReactNode;
}

interface State {
  readonly error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: unknown): State {
    return { error: error instanceof Error ? error : new Error(String(error)) };
  }

  override componentDidCatch(error: unknown, info: ErrorInfo): void {
    // Surface to the console for local debugging; the SPA sends nothing off-origin.
    console.error("Unhandled UI error", error, info.componentStack);
  }

  private readonly reload = (): void => {
    window.location.reload();
  };

  override render(): ReactNode {
    const { error } = this.state;
    if (error === null) return this.props.children;
    return (
      <div className="boundary" role="alert">
        <h1>Something went wrong</h1>
        <p>
          The interface hit an unexpected error. Your data is unaffected; reloading usually clears
          it.
        </p>
        <p style={{ color: "var(--text-faint)" }}>{error.message}</p>
        <button type="button" className="control__button" onClick={this.reload}>
          Reload
        </button>
      </div>
    );
  }
}
